import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../../../../../components/global/DropdownMenu';
import FeatureBlock from '../../../../../../components/contextual/FeatureBlock';
import { canAccessMyMatchesFromWebGallery } from '../../../../../../util/product-permissions';
import MyMatchesButton from '../../../../../../components/global/MyMatchesButton';
import { getAsset } from '../../../../../../util/assets';
import Button from '../../../../../../components/global/Button';
import HomeIcon from '../../../../../../components/icons/HomeIcon';
import InviteIcon from '../../../../../../components/icons/InviteIcon';
import ContributeButton from '../../../../../../components/global/ContributeButton';
import UploadIcon from '../../../../../../components/icons/UploadIcon';
import NotificationCounter from '../../../../../pro/components/NotificationCounter';
import BlogIcon from '../../../../../../components/icons/BlogIcon';
import VideoIcon from '../../../../../../components/icons/VideoIcon';
import FavoritesIcon from '../../../../../../components/icons/FavoritesIcon';
import DotTrigger from '../../../../../../components/global/DotTrigger';
import { ALBUM_TYPE } from '../../../../../../constants/albums';
import useUploadPermission from '../../../../../pro/services/contributors/hooks/useUploadPermission';
import useVideoUploadPermission from '../../../../../pro/services/contributors/hooks/useVideoUploadPermission';
import { canContributeToEntity } from '../../../../../../components/global/ContributeButton/helpers';

const propTypes = {
  authenticated: PropTypes.bool.isRequired,
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  blogUrl: PropTypes.string,
  videosUrl: PropTypes.string,
  favoritesUrl: PropTypes.string,
  onFavoritesClick: PropTypes.func,
  onInviteClick: PropTypes.func,
  videosTotal: PropTypes.number,
  blogPostsTotal: PropTypes.number,
  unreadBlogPosts: PropTypes.number,
  backBtnLabel: PropTypes.string,
  backBtnLink: PropTypes.string,
  isOwner: PropTypes.bool,
};

const WebGalleryActionDropdown = ({
  authenticated,
  album,
  albumFolder,
  blogUrl,
  videosUrl,
  favoritesUrl,
  onFavoritesClick,
  onInviteClick,
  videosTotal,
  blogPostsTotal,
  unreadBlogPosts,
  backBtnLabel,
  backBtnLink,
  isOwner,
}) => {
  const entity = album || albumFolder;
  const { isAllowed } = useUploadPermission({
    entityId: entity?.id,
  });
  const { isAllowed: isAllowedToUploadVideo } = useVideoUploadPermission({
    entityId: entity?.id,
    // Important to check for ownership here. Otherwise, we'll assume the user is
    // the owner when it isn't a contributor
    checkOwnership: true,
  });
  const { enable_external_upload: externalUpload } = entity;
  const entityIsEvent = entity.album_type === ALBUM_TYPE.EVENT;

  const uploadAllowed = !!isAllowed || (entityIsEvent && externalUpload);
  const videoUploadAllowed =
    !!isAllowedToUploadVideo || (entityIsEvent && externalUpload);

  const canAccessBack =
    isOwner || (!authenticated && albumFolder?.album_type === ALBUM_TYPE.EVENT);

  const shouldRender = useMemo(() => {
    if (canAccessBack && backBtnLink) {
      return true;
    }

    if (canAccessMyMatchesFromWebGallery(albumFolder || album)) {
      return true;
    }

    if (authenticated && entity?.album_type === ALBUM_TYPE.SHUTTERBUG) {
      return true;
    }

    if (canContributeToEntity(entity, uploadAllowed, videoUploadAllowed)) {
      return true;
    }

    if (blogPostsTotal > 0 && blogUrl) {
      return true;
    }

    if (videosTotal > 0 && videosUrl && !album?.is_video_album) {
      return true;
    }

    if (favoritesUrl) {
      return true;
    }

    return false;
  }, [
    album,
    albumFolder,
    entity,
    authenticated,
    blogPostsTotal,
    blogUrl,
    videosTotal,
    videosUrl,
    favoritesUrl,
  ]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div className="dropdown-menu-dark m-0">
      <DropdownMenu
        placement="bottom-end"
        toggle={<DotTrigger dotType="vertical" />}
      >
        <FeatureBlock
          condition={canAccessMyMatchesFromWebGallery(albumFolder || album)}
        >
          <MyMatchesButton
            className="wg-my-matches-btn dropdown-item sidebar-item"
            albumMembershipGroupId={album?.id}
            albumFolderId={
              albumFolder?.root?.id ??
              album?.album_folder?.root?.id ??
              album?.root_album_folder?.id
            }
            btnStyle="reset"
            disableWithoutEntitlement={
              (albumFolder && !albumFolder.joins_enabled) ||
              (album && !album.root_album_folder?.joins_enabled)
            }
          >
            <div className="d-flex align-items-center">
              <img src={getAsset('/waldo-magnified.svg')} alt="Waldo" />{' '}
              <span>Activate auto&ndash;tagging</span>
            </div>
          </MyMatchesButton>
        </FeatureBlock>
        {canAccessBack && backBtnLink && (
          <Button
            btnStyle="reset"
            className="dropdown-item sidebar-item"
            to={backBtnLink}
          >
            <HomeIcon size={16} color="#fff" className="mx-1" /> {backBtnLabel}
          </Button>
        )}
        {authenticated && entity?.album_type === ALBUM_TYPE.SHUTTERBUG && (
          <>
            <Button
              btnStyle="reset"
              className="dropdown-item sidebar-item"
              onClick={onInviteClick}
            >
              <InviteIcon size={16} color="#fff" className="mx-1" /> Invite
            </Button>
          </>
        )}
        <ContributeButton>
          {({ onClick }) => (
            <Button
              btnStyle="reset"
              className="dropdown-item sidebar-item"
              onClick={onClick}
            >
              <UploadIcon size={16} color="#fff" className="mx-1" /> Upload
            </Button>
          )}
        </ContributeButton>
        {blogPostsTotal > 0 && blogUrl && (
          <Button
            btnStyle="reset"
            className="dropdown-item sidebar-item"
            to={blogUrl}
          >
            <NotificationCounter
              Icon={BlogIcon}
              count={
                unreadBlogPosts > 0 && authenticated
                  ? unreadBlogPosts
                  : undefined
              }
              size={16}
              color="#fff"
              className="mx-1"
            />
            Blog
          </Button>
        )}
        {videosTotal > 0 && videosUrl && !album?.is_video_album && (
          <Button
            btnStyle="reset"
            className="dropdown-item sidebar-item"
            to={videosUrl}
          >
            <VideoIcon size={16} color="#fff" className="mx-1" /> Videos
          </Button>
        )}
        {favoritesUrl && (
          <Button
            btnStyle="reset"
            className="dropdown-item sidebar-item"
            onClick={onFavoritesClick}
          >
            <FavoritesIcon size={16} color="#fff" className="mx-1" /> Favorites
          </Button>
        )}
      </DropdownMenu>
    </div>
  );
};

WebGalleryActionDropdown.propTypes = propTypes;

export default WebGalleryActionDropdown;
